import {
    sanitizeComposeHtml,
    newContentId,
    ATTACHMENT_MAX_CHARS,
    INLINE_IMAGE_ALLOWED_TYPES,
    INLINE_IMAGE_MAX_BYTES,
} from "./composeSanitize";
import { wrapSendBodyFont } from "./sendBodyFont";
import { listenEbenenAngleichen } from "./richtext/listenEbenen";
import { tabsFuerVersand } from "./richtext/tabulator";

/**
 * Der Sendepfad des Verfassen-Fensters als pure, testbare Funktion.
 *
 * SP1 hat die Pipeline unverändert aus `ComposeMailWindow.handleSend` hierher
 * verschoben. SP2 ergänzt die `data:`→`cid`-Konvertierung — mit einer harten
 * Einschränkung, die den Rest der Datei prägt:
 *
 * ══ DER ZITIERTE VERLAUF IST TABU ══════════════════════════════════════════
 * Der Lesepfad schreibt eingehende `cid:`-Inline-Bilder server-seitig zu
 * `data:`-URIs um (`graph-mail.service.ts: rewriteInlineCidToDataUri`), und
 * `buildReplyQuote` bettet genau diesen Body als Zitat ein. Ein naiver Lauf über
 * alle `img[src^="data:"]` würde deshalb beim Antworten auf eine Mail mit
 * Inline-Logo den KOMPLETTEN zitierten Verlauf zu Anhängen der eigenen Antwort
 * machen — kumulierend über jede Thread-Runde, bis der Größen-Guard den Versand
 * blockiert. Der Nutzer könnte das nicht beheben, weil er diese Bilder nie
 * eingefügt hat.
 *
 * Deshalb: Alles unterhalb des Zitat-Knotens bleibt bitgenau unverändert —
 * weder Konvertierung noch Aufräumen fasst es an. Das ist der Status quo und
 * damit garantiert regressionsfrei.
 * ═══════════════════════════════════════════════════════════════════════════
 */

/** Markiert den zitierten Verlauf (gesetzt in `MailDetailWindow.buildReplyQuote`). */
const QUOTE_ATTR = "data-kmvs-quote";

/** `data:<mime>[;param]*,<payload>` — zerlegt in MIME, Parameter und Nutzlast. */
const DATA_URI_RE = /^data:([^;,]*)((?:;[^;,]*)*),([\s\S]*)$/i;

/** Identisch zur Backend-Validierung in `SendMailAttachmentDto` (mail.dto.ts). */
const BASE64_RE = /^[A-Za-z0-9+/]+={0,2}$/;

/** Backend-Grenze für `contentBytes` (`@MaxLength`) — hier als Sicherheitsnetz.
 *  Eine Quelle: der Wert lebt in `composeSanitize`, damit die Grenze nicht an
 *  drei Stellen auseinanderlaufen kann. */
const ATTACHMENT_BASE64_MAX_CHARS = ATTACHMENT_MAX_CHARS;

/** Im Editor getracktes Inline-Bild (SP6, C3). */
export type InlineImage = {
    /** Content-Id (cid) — matcht `<img src="cid:<id>">` nach dem Transform. */
    contentId: string;
    /** Blob-URL für die Editor-Anzeige (wird beim Unmount freigegeben). */
    blobUrl: string;
    contentType: string;
    /** Base64 OHNE data:-Präfix (wird gesendet). */
    base64: string;
    name: string;
};

/** Ein ausgehender Inline-Anhang (Wire-Shape von `SendMailAttachment`, isInline). */
export type OutgoingAttachment = {
    name: string;
    contentType: string;
    contentBytes: string;
    isInline: true;
    contentId: string;
};

/**
 * Transformiert alle getrackten Inline-Bilder im HTML:
 * `<img ... data-cid="X" src="blob:...">` → `<img ... src="cid:X">`.
 *
 * Bleibt als eigenständige Funktion erhalten (SP1-Vertrag, eigene Tests);
 * `buildSendBody` macht dieselbe Arbeit inzwischen zitat-bewusst selbst.
 */
export function transformInlineImages(html: string): { html: string; usedCids: Set<string> } {
    const usedCids = new Set<string>();
    if (typeof document === "undefined") return { html, usedCids };
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    tpl.content.querySelectorAll("img[data-cid]").forEach((el) => {
        const cid = el.getAttribute("data-cid");
        if (!cid) return;
        el.setAttribute("src", `cid:${cid}`);
        el.removeAttribute("data-cid");
        usedCids.add(cid);
    });
    return { html: tpl.innerHTML, usedCids };
}

/**
 * Findet den Zitat-Container — bewusst NUR als direktes Kind des Wurzelknotens.
 *
 * Diese Einschränkung ist eine Sicherheitsmaßnahme: Läge der Marker irgendwo
 * tief im Baum, könnte zitiertes Fremd-HTML einen eigenen „Zitat"-Container
 * vortäuschen und so bestimmen, welche Teile der Mail unangetastet bleiben.
 *
 * Der `blockquote`/`border-left`-Zweig ist der Fallback für Verfassen-Fenster,
 * die vor SP2 geöffnet wurden (deren Zitat trägt den Marker noch nicht) sowie
 * für Zitate fremder Mailclients.
 */
export function findQuoteRoot(root: ParentNode): Element | null {
    const kids = Array.from(root.children);
    for (const el of kids) if (el.hasAttribute(QUOTE_ATTR)) return el;
    for (const el of kids) {
        if (el.tagName === "BLOCKQUOTE") return el;
        if (/border-left/i.test(el.getAttribute("style") ?? "")) return el;
    }
    return null;
}

/**
 * Zerlegt einen `data:`-URI in MIME-Typ und normalisiertes Base64.
 *
 * Normalisiert dabei, was Browser und Fremdclients real liefern: Zeilenumbrüche
 * im Payload, base64url-Alphabet (`-`/`_`) und fehlendes Padding. Prozent-
 * kodierte (nicht-base64) `data:`-URIs sind nicht konvertierbar und werden
 * abgelehnt, statt sie zu zerstören.
 */
export function decodeDataUri(
    src: string,
):
    | { ok: true; contentType: string; base64: string; bytes: number }
    | { ok: false; reason: string } {
    const m = DATA_URI_RE.exec(src.trim());
    if (!m) return { ok: false, reason: "kein data:-URI" };

    let mime = (m[1] || "").trim().toLowerCase();
    if (mime === "image/jpg") mime = "image/jpeg"; // Alt-Clients schreiben image/jpg

    if (!/(^|;)base64(;|$)/.test((m[2] || "").toLowerCase())) {
        return { ok: false, reason: "nicht base64-kodiert" };
    }

    let p = m[3].replace(/\s+/g, "").replace(/-/g, "+").replace(/_/g, "/");
    const rest = p.length % 4;
    if (rest === 1) return { ok: false, reason: "ungültige base64-Länge" };
    if (rest) p += "=".repeat(4 - rest);
    if (!p || !BASE64_RE.test(p)) return { ok: false, reason: "ungültiges base64-Alphabet" };

    const pad = p.endsWith("==") ? 2 : p.endsWith("=") ? 1 : 0;
    return { ok: true, contentType: mime, base64: p, bytes: (p.length / 4) * 3 - pad };
}

/** Alle `cid:`-Referenzen, die im HTML tatsächlich noch vorkommen. */
export function collectCidRefs(html: string): Set<string> {
    const out = new Set<string>();
    if (typeof document === "undefined") return out;
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    tpl.content.querySelectorAll('img[src^="cid:"]').forEach((el) =>
        out.add((el.getAttribute("src") ?? "").slice(4)),
    );
    return out;
}

/** Dateiendung für den Anhangsnamen. */
const extFor = (mime: string): string =>
    mime === "image/jpeg" ? "jpg" : mime === "image/svg+xml" ? "svg" : mime.split("/")[1] || "img";

/**
 * Anhangsname aus dem `alt`-Text, sonst `bild-<n>.<ext>`. Muss die Backend-
 * Regel `@Length(1, 255)` erfüllen; Steuerzeichen werden entfernt.
 */
export function attachmentName(img: Element, n: number, mime: string): string {
    const alt = (img.getAttribute("alt") ?? "")
        // eslint-disable-next-line no-control-regex
        .replace(/[\u0000-\u001F\u007F]/g, "")
        .trim()
        .slice(0, 200);
    return alt || `bild-${n}.${extFor(mime)}`;
}

/**
 * Entfernt `src` von `cid:`-Bildern, für die es keinen Anhang gibt — sonst zeigt
 * der Empfänger ein kaputtes Bild-Icon. AUSSERHALB des Zitats; im Zitat bleibt
 * alles unverändert (dort sind `cid:`-Referenzen fremder Mails legitim).
 */
export function stripUnresolvedCidsOutsideQuote(html: string, keep: Set<string>): string {
    if (typeof document === "undefined") return html;
    const tpl = document.createElement("template");
    tpl.innerHTML = html;
    const quote = findQuoteRoot(tpl.content);
    tpl.content.querySelectorAll('img[src^="cid:"]').forEach((el) => {
        if (quote && quote.contains(el)) return;
        const cid = (el.getAttribute("src") ?? "").slice(4);
        if (!keep.has(cid)) el.removeAttribute("src");
    });
    return tpl.innerHTML;
}

/**
 * Baut Body + Inline-Anhänge für den Versand.
 *
 * Ablauf:
 *   A. Zitat bestimmen — alles darunter ist für die weiteren Schritte tabu.
 *   B. `blob:`-Pfad: getrackte Editor-Bilder (`data-cid`) → `cid:`.
 *   C. `data:`-Pfad: eingebettete Bilder (Signatur, Zwischenablage) → `cid:`,
 *      mit Dedup über die Nutzlast, damit dasselbe Logo nur EINMAL anhängt.
 *   C2. Gliederungszeichen der Listen und Tab-Hüllen für Outlook (T-346).
 *   D. Sanitizing (kann `<img>` entfernen).
 *   E. Verwendete cids ERST DANACH ermitteln — deckt „vom Nutzer gelöscht" und
 *      „vom Sanitizer entfernt" mit derselben Logik ab.
 *   F. Unauflösbare `cid:`-Referenzen außerhalb des Zitats entschärfen.
 *
 * `warnings` sind rein informativ — ein nicht konvertierbares Bild blockiert den
 * Versand nie, es bleibt dann eingebettet (Status quo).
 */
export function buildSendBody(
    rawHtml: string,
    inlineImages: InlineImage[],
): { body: string; attachments: OutgoingAttachment[]; warnings: string[] } {
    const warnings: string[] = [];
    if (typeof document === "undefined") return { body: rawHtml, attachments: [], warnings };

    const tpl = document.createElement("template");
    tpl.innerHTML = rawHtml;
    const frag = tpl.content;

    // (A) Zitat bestimmen — ALLES darunter bleibt unangetastet.
    const quote = findQuoteRoot(frag);
    const outsideQuote = (el: Element) => !quote || !quote.contains(el);

    const candidates = new Map<string, OutgoingAttachment>(); // contentId → Anhang
    const byPayload = new Map<string, string>(); // `${mime}|${base64}` → contentId

    // (B) blob:-Pfad — die im Editor getrackten Bilder (unverändertes Verhalten).
    //     Zwei Durchläufe, weil die REIHENFOLGE der Anhänge `inlineImages` folgt
    //     und nicht dem Vorkommen im Dokument (so war es vor SP2, ein
    //     Charakterisierungstest hält das fest).
    const foundCids = new Set<string>();
    for (const img of Array.from(frag.querySelectorAll("img[data-cid]"))) {
        if (!outsideQuote(img)) continue;
        const cid = img.getAttribute("data-cid");
        img.removeAttribute("data-cid");
        if (!cid) continue;
        if (!inlineImages.some((i) => i.contentId === cid)) continue; // verwaist → src bleibt
        img.setAttribute("src", `cid:${cid}`);
        foundCids.add(cid);
    }
    for (const meta of inlineImages) {
        if (!foundCids.has(meta.contentId)) continue;
        candidates.set(meta.contentId, {
            name: meta.name,
            contentType: meta.contentType,
            contentBytes: meta.base64,
            isInline: true,
            contentId: meta.contentId,
        });
        byPayload.set(`${meta.contentType}|${meta.base64}`, meta.contentId);
    }

    // (C) data:-Pfad — Signaturbilder und Einfügungen aus der Zwischenablage.
    let n = 0;
    for (const img of Array.from(frag.querySelectorAll('img[src^="data:"]'))) {
        if (!outsideQuote(img)) continue;

        const dec = decodeDataUri(img.getAttribute("src") ?? "");
        if (!dec.ok) {
            warnings.push(`Ein eingebettetes Bild wurde nicht umgewandelt (${dec.reason}).`);
            continue;
        }
        if (!(INLINE_IMAGE_ALLOWED_TYPES as readonly string[]).includes(dec.contentType)) {
            warnings.push(`Bildformat ${dec.contentType} wird nicht als Inline-Anhang unterstützt.`);
            continue;
        }
        if (dec.bytes > INLINE_IMAGE_MAX_BYTES) {
            warnings.push(
                `Ein eingebettetes Bild ist größer als ${Math.round(INLINE_IMAGE_MAX_BYTES / 1024)} KB und bleibt eingebettet.`,
            );
            continue;
        }
        if (dec.base64.length > ATTACHMENT_BASE64_MAX_CHARS) {
            warnings.push("Ein eingebettetes Bild überschreitet die Anhang-Grenze und bleibt eingebettet.");
            continue;
        }

        const key = `${dec.contentType}|${dec.base64}`;
        let cid = byPayload.get(key); // Dedup: dasselbe Logo = EIN Anhang
        if (!cid) {
            cid = newContentId();
            byPayload.set(key, cid);
            candidates.set(cid, {
                name: attachmentName(img, ++n, dec.contentType),
                contentType: dec.contentType,
                contentBytes: dec.base64,
                isInline: true,
                contentId: cid,
            });
        }
        img.setAttribute("src", `cid:${cid}`);
    }

    // (C2) Outlook-Darstellung absichern (T-346) — außerhalb des Zitats:
    //      Gliederungszeichen je Listenebene inline (auch für Vorlagen- und
    //      Signatur-Inhalte, die nie durch den Editor liefen) und Tab-Hüllen
    //      mit `mso-tab-count` für Outlook Desktop.
    listenEbenenAngleichen(frag, { ausserhalb: quote });
    tabsFuerVersand(frag, quote);

    // (D) Sanitizer — kann <img> entfernen.
    const safe = sanitizeComposeHtml(tpl.innerHTML);

    // (E) Verwendete cids ERST JETZT lesen. Die contentIds sind frische uuids,
    //     eine Kollision mit cid:-Referenzen aus dem Zitat ist ausgeschlossen.
    const used = collectCidRefs(safe);
    const attachments = [...candidates.values()].filter((a) => used.has(a.contentId));
    const keep = new Set(attachments.map((a) => a.contentId));

    // (F) cid-Referenzen ohne Anhang entschärfen — wieder nur außerhalb des Zitats.
    return {
        body: wrapSendBodyFont(stripUnresolvedCidsOutsideQuote(safe, keep)),
        attachments,
        warnings,
    };
}
